import type { PlacePhraseFormatter, PlacePhraseIntent } from '../types'

const FRENCH_COUNTRY_NAMES_USING_AUX = new Set([
  'bahamas',
  'comores',
  'émirats arabes unis',
  'états-unis',
  'états-unis d’amérique',
  'fidji',
  'îles marshall',
  'îles salomon',
  'maldives',
  'palaos',
  'pays-bas',
  'philippines',
  'seychelles',
  'tonga',
])

/**
 * Countries that take "au" article
 */
const FRENCH_COUNTRY_NAMES_USING_AU = new Set([
  'bangladesh',
  'bélarus',
  'bélize',
  'bénin',
  'bhoutan',
  'botswana',
  'brésil',
  'burkina faso',
  'burundi',
  'cameroun',
  'canada',
  'chili',
  'congo',
  'costa rica',
  'danemark',
  'gabon',
  'ghana',
  'guatemala',
  'guyana',
  'honduras',
  'japon',
  'kazakhstan',
  'kenya',
  'kirghizistan',
  'kosovo',
  'koweït',
  'laos',
  'lesotho',
  'liban',
  'libéria',
  'liechtenstein',
  'luxembourg',
  'malawi',
  'mali',
  'maroc',
  'mexique',
  'monténégro',
  'cambodge',
  'mozambique',
  'népal',
  'nicaragua',
  'niger',
  'nigéria',
  'pakistan',
  'panamá',
  'paraguay',
  'pérou',
  'portugal',
  'qatar',
  'royaume-uni',
  'rwanda',
  'saint-siège',
  'salvador',
  'samoa',
  'sénégal',
  'soudan',
  'soudan du sud',
  'sri lanka',
  'suriname',
  'swaziland',
  'tadjikistan',
  'tchad',
  'timor-oriental',
  'togo',
  'turkménistan',
  'venezuela',
  'vietnam',
  'yémen',
  'zimbabwe',
])

/**
 * * Countries that take "en" article
 */
const FRENCH_COUNTRY_NAMES_USING_EN = new Set([
  'afghanistan',
  'afrique du sud',
  'albanie',
  'algérie',
  'allemagne',
  'andorre',
  'angola',
  'arabie saoudite',
  'argentine',
  'arménie',
  'australie',
  'autriche',
  'azerbaïdjan',
  'belgique',
  'birmanie',
  'bolivie',
  'bosnie-herzégovine',
  'bulgarie',
  'chine',
  'colombie',
  'corée du nord',
  'corée du sud',
  'côte d’ivoire',
  'croatie',
  'égypte',
  'équateur',
  'érythrée',
  'espagne',
  'estonie',
  'éthiopie',
  'finlande',
  'france',
  'gambie',
  'géorgie',
  'grèce',
  'guinée',
  'guinée-bissau',
  'guinée équatoriale',
  'hongrie',
  'inde',
  'indonésie',
  'iran',
  'iraq',
  'irlande',
  'islande',
  'israël',
  'italie',
  'jordanie',
  'lettonie',
  'libye',
  'lituanie',
  'macédoine',
  'malaisie',
  'mauritanie',
  'micronésie',
  'moldavie',
  'namibie',
  'norvège',
  'nouvelle-zélande',
  'ouganda',
  'ouzbékistan',
  'papouasie-nouvelle-guinée',
  'pologne',
  'république centrafricaine',
  'république démocratique du congo',
  'république dominicaine',
  'république tchèque',
  'roumanie',
  'russie',
  'serbie',
  'sierra leone',
  'slovaquie',
  'slovénie',
  'somalie',
  'suède',
  'suisse',
  'syrie',
  'tanzanie',
  'thaïlande',
  'tunisie',
  'turquie',
  'ukraine',
  'uruguay',
  'zambie',
])
const FRENCH_VOWEL_OR_H_PATTERN = /^[aeiouyàâæéèêëîïôœùûüÿh]/i

type FrenchPlaceArticle = 'city' | 'feminine' | 'feminineWithArticle' | 'masculine' | 'plural' | 'elided'

interface FrenchPlaceGrammar {
  article: FrenchPlaceArticle
  name: string
}

function startsWithFrenchVowelOrH(name: string): boolean {
  return FRENCH_VOWEL_OR_H_PATTERN.test(name)
}

function getFrenchPlaceGrammar(name: string, locale: string): FrenchPlaceGrammar {
  // checks if name already contains French article
  const articleMatch = /^(le|la|les)\s+(.+)$/i.exec(name) ?? /^(l[’'])(.+)$/i.exec(name)
  if (articleMatch) {
    const article = articleMatch[1].toLocaleLowerCase(locale)
    const rest = articleMatch[2]
    if (article === 'le') return { article: 'masculine', name: rest }
    if (article === 'les') return { article: 'plural', name: rest }
    if (article === 'la') return { article: 'feminineWithArticle', name }

    return { article: 'elided', name: rest }
  }

  const key = name.toLocaleLowerCase(locale)
  if (FRENCH_COUNTRY_NAMES_USING_AUX.has(key)) return { article: 'plural', name }
  if (FRENCH_COUNTRY_NAMES_USING_AU.has(key)) return { article: 'masculine', name }
  if (FRENCH_COUNTRY_NAMES_USING_EN.has(key)) return { article: 'feminine', name }

  return { article: 'city', name }
}

/**
 * Format a place name according to grammar rules listed in
 * https://apprendre.tv5monde.com/en/aides/grammar-prepositions-au-en-aux-name-city-or-country
 */
function formatFrenchPlaceName(name: string, locale: string, intent: PlacePhraseIntent): string {
  if (intent === 'display') return name

  const grammar = getFrenchPlaceGrammar(name, locale)
  const placeName = grammar.name
  if (intent === 'location' || intent === 'destination') {
    if (grammar.article === 'plural') return `aux ${placeName}`
    if (grammar.article === 'masculine') return `au ${placeName}`
    if (grammar.article === 'feminine') return `en ${placeName}`
    if (grammar.article === 'elided') return `à l’${placeName}`
    if (grammar.article === 'feminineWithArticle') return `à ${placeName}`

    return `à ${placeName}`
  }

  if (grammar.article === 'plural') return `des ${placeName}`
  if (grammar.article === 'masculine') return `du ${placeName}`
  if (grammar.article === 'elided') return `de l’${placeName}`
  if (intent === 'origin') return startsWithFrenchVowelOrH(placeName) ? `d’${placeName}` : `de ${placeName}`
  if (grammar.article === 'feminine') return startsWithFrenchVowelOrH(placeName) ? `de l’${placeName}` : `de la ${placeName}`
  if (grammar.article === 'feminineWithArticle') return `de ${placeName}`

  return startsWithFrenchVowelOrH(placeName) ? `d’${placeName}` : `de ${placeName}`
}

const frenchPlacePhraseFormatter: PlacePhraseFormatter = {
  format: (place, locale, intent) => formatFrenchPlaceName(place.name, locale, intent),
}

export default frenchPlacePhraseFormatter
